import {
  isSubscribedToOfficialAccount,
  requestOfficialAccountFriend,
} from '../chatbot/chatbot.api';
import { type ConsentSurface } from './onboarding.analytics';
import { isInLineMiniApp } from '@play-co/gcinstant';
import {
  incentivizedOAReminderLevel,
  incentivizedOAStartLevel,
} from 'src/replicant/chatbot/chatbot.ruleset';
import {
  showIncentivizedOAHalfModal,
  showIncentivizedOALevel15Reminder,
} from '../chatbot/incentivizedOA.sequence';

/**
 * SC in-game Add-Friend popup, resolved from A/B 0477 to `add_friend_twice`.
 *
 * Adds extra OA Add-Friend asks at high-intent in-game moments for users who are
 * still unsubscribed: post-tutorial and Puzzle 5 win.
 *
 * Reuses the exact Add-Friend overlay + analytics of the splash arm:
 * `requestOfficialAccountFriend(origin, { forceServerSide: true })`.
 */

/**
 * Post-tutorial ask, right as the FTUE tutorial completes (see
 * `puzzleCoinsPostToyCareSequence`).
 */
export async function inGamePopupAfterTutorial(): Promise<void> {
  await requestInGameOAFriendIfUnsubscribed('post_tutorial');
}

/**
 * Post-level-win ask. The caller (match2 post-game sequence) only invokes this
 * on a win, outside the tutorial.
 */
export async function inGamePopupAfterLevelWin(level: number): Promise<void> {
  // Mini App users receive the resolved reward-bearing offer after Puzzle 5.
  if (level === incentivizedOAStartLevel) {
    if (isInLineMiniApp()) {
      await showIncentivizedOAHalfModal('level_5');
      return;
    }

    // Other supported clients retain the resolved A/B 0477 Add-Friend prompt.
    await requestInGameOAFriendIfUnsubscribed('level_5');
    return;
  }

  // Eligible Mini App users receive one persistent reminder after Puzzle 15.
  if (level === incentivizedOAReminderLevel && isInLineMiniApp()) {
    await showIncentivizedOALevel15Reminder();
  }
}

/**
 * Show the Add-Friend overlay only while the user is still unsubscribed. The
 * "still unsubscribed" check uses the authoritative server-side round-trip (per
 * spec: "Detected using the server-side API"), so the nudge stops as soon as the
 * user subscribes — even mid-session, via this popup or the launch baseline.
 * Forces the server path on the overlay's own verification too, so it can never
 * pop the LINE consent popover.
 */
async function requestInGameOAFriendIfUnsubscribed(
  origin: ConsentSurface,
): Promise<void> {
  if (await isSubscribedToOfficialAccount({ forceServerSide: true })) return;

  await requestOfficialAccountFriend(origin, { forceServerSide: true });
}
